// In-app task creation via approved Task Types.
//
// Replaces the previous flow where "New task" opened an external
// Airtable form. The picker fetches available Task Type templates for
// the user's base, lets them search/select one, and POSTs to
// /api/create-task which lands a new Task Queue record + lets Airtable
// automation populate the sub-tasks. The user is dropped into the new
// task automatically on success.

import { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api.js';

export default function TaskTypePicker({ uid, onCreated, onCancel, fallbackFormUrl }) {
  const [query, setQuery] = useState('');
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [creating, setCreating] = useState(null); // taskTypeId currently being created
  const [createError, setCreateError] = useState(null);
  const inputRef = useRef(null);
  const reqId = useRef(0);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search — 200ms matches TaskSearch's pattern.
  useEffect(() => {
    const myReq = ++reqId.current;
    const t = setTimeout(() => {
      setLoading(true);
      setLoadError(null);
      api.taskTypes(query)
        .then(d => {
          if (reqId.current === myReq) {
            setTaskTypes(d.taskTypes || []);
            setLoading(false);
          }
        })
        .catch(e => {
          if (reqId.current === myReq) {
            setLoadError(e.message);
            setLoading(false);
          }
        });
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const handlePick = async (taskType) => {
    if (creating) return;
    setCreating(taskType.id);
    setCreateError(null);
    try {
      const res = await api.createTask(taskType.id, uid);
      onCreated?.(res.task);
    } catch (e) {
      setCreateError(e.message);
      setCreating(null);
    }
  };

  return (
    <div className="rounded-lg border border-accent/40 bg-panel overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-panel2/50 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">New task</div>
        <button
          onClick={onCancel}
          className="text-muted hover:text-text text-xs flex items-center gap-1"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
          Cancel
        </button>
      </div>

      <div className="p-3">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search task types..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          disabled={Boolean(creating)}
          className="w-full px-3 py-2 rounded-md bg-panel border border-border focus:border-accent focus:outline-none text-sm placeholder:text-muted"
        />

        {loadError && (
          <div className="mt-3 text-xs text-danger">{loadError}</div>
        )}

        {createError && (
          <div className="mt-3 p-2 rounded border border-danger/30 bg-danger/10 text-xs text-danger">
            {createError}
          </div>
        )}
      </div>

      <div className="max-h-[40vh] overflow-y-auto divide-y divide-border">
        {loading && taskTypes.length === 0 && (
          <div className="p-4 text-center text-xs text-muted">Loading task types…</div>
        )}
        {!loading && taskTypes.length === 0 && (
          <div className="p-4 text-center text-xs text-muted">
            No task types match.
            {fallbackFormUrl && (
              <>
                {' '}Need a non-standard task?{' '}
                <a
                  href={fallbackFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-accent hover:text-accent2"
                >
                  Use the intake form ↗
                </a>
              </>
            )}
          </div>
        )}
        {taskTypes.map(t => {
          const isCreating = creating === t.id;
          const disabled = Boolean(creating) && !isCreating;
          return (
            <button
              key={t.id}
              onClick={() => handlePick(t)}
              disabled={disabled || isCreating}
              className={`w-full text-left px-4 py-3 hover:bg-panel2 transition-colors ${
                isCreating ? 'bg-accent/10' : disabled ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-[11px] text-muted mt-0.5 flex items-center gap-2">
                    {t.team && <span>{t.team}</span>}
                    {t.estimatedMinutes != null && <span>· ~{t.estimatedMinutes}m</span>}
                    {t.subTaskTemplateIds && t.subTaskTemplateIds.length > 0 && (
                      <span>· {t.subTaskTemplateIds.length} sub-task{t.subTaskTemplateIds.length === 1 ? '' : 's'}</span>
                    )}
                  </div>
                </div>
                {isCreating ? (
                  <span className="text-xs text-accent font-medium flex-shrink-0">Creating…</span>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted flex-shrink-0">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
